import type { TypePrerequis } from "./contract";

/**
 * Mise en forme des valeurs du portefeuille. Tout est pur et sans horloge implicite :
 * l'instant courant est toujours passe en argument, pour que les tests soient stables et
 * que la page ne change pas de texte entre deux rendus.
 *
 * Les dates arrivent sous deux formes (piege connu du ticket 2) : `AAAA-MM-JJ` pour une
 * fiche, horodatage complet pour le scout. Le jour est donc extrait du texte quand il est
 * la, plutot que de passer par `new Date`, qui lirait minuit en temps universel et
 * afficherait la veille dans un fuseau negatif.
 */

const MS_PAR_JOUR = 86_400_000;

const JOUR = /^(\d{4})-(\d{2})-(\d{2})/;

/** Numero de jour en temps universel, ou `null` si la valeur n'est pas une date. */
function numeroDeJour(valeur: string): number | null {
  const trouve = JOUR.exec(valeur);
  if (trouve) {
    return Math.floor(
      Date.UTC(Number(trouve[1]), Number(trouve[2]) - 1, Number(trouve[3])) / MS_PAR_JOUR,
    );
  }
  const analyse = Date.parse(valeur);
  return Number.isNaN(analyse) ? null : Math.floor(analyse / MS_PAR_JOUR);
}

/** `2026-09-13` et `2026-09-13T10:00:00Z` donnent tous deux `13/09/2026`. */
export function formatDate(valeur: string | null | undefined): string {
  if (!valeur) return "";
  const trouve = JOUR.exec(valeur);
  if (trouve) return `${trouve[3]}/${trouve[2]}/${trouve[1]}`;
  const analyse = Date.parse(valeur);
  if (Number.isNaN(analyse)) return valeur;
  const date = new Date(analyse);
  const deuxChiffres = (nombre: number): string => String(nombre).padStart(2, "0");
  return `${deuxChiffres(date.getUTCDate())}/${deuxChiffres(date.getUTCMonth() + 1)}/${date.getUTCFullYear()}`;
}

/** Nombre de jours entiers entre une date et maintenant. Negatif : la date est a venir. */
export function joursDepuis(depuis: string | null | undefined, maintenant: number): number | null {
  if (!depuis) return null;
  const jour = numeroDeJour(depuis);
  if (jour === null) return null;
  return Math.floor(maintenant / MS_PAR_JOUR) - jour;
}

/** Depuis combien de temps un prerequis attend. */
export function ageEnClair(depuis: string | null | undefined, maintenant: number): string {
  const jours = joursDepuis(depuis, maintenant);
  if (jours === null) return "sans date";
  if (jours < 0) return "à venir";
  if (jours === 0) return "aujourd'hui";
  if (jours === 1) return "depuis 1 jour";
  return `depuis ${jours} jours`;
}

/**
 * L'age d'un geste dans une colonne etroite : `9 j`. Le texte entier (`ageEnClair`) reste
 * au survol, la ou ce raccourci est affiche.
 */
export function ageCourt(depuis: string | null | undefined, maintenant: number): string {
  const jours = joursDepuis(depuis, maintenant);
  if (jours === null) return "sans date";
  if (jours < 0) return "à venir";
  return `${jours} j`;
}

/**
 * Au-dela d'une semaine, un geste a manque au moins une seance de travail : il passe en
 * rouge. Plus bas, tout serait rouge en permanence et la couleur ne dirait plus rien.
 */
export const SEUIL_ANCIEN_JOURS = 7;

/** Un prerequis qui attend depuis une semaine ou plus. Sans date, on ne sait pas : false. */
export function estAncien(depuis: string | null | undefined, maintenant: number): boolean {
  const jours = joursDepuis(depuis, maintenant);
  return jours !== null && jours >= SEUIL_ANCIEN_JOURS;
}

/**
 * Trois mois : au-dela, un guide a de bonnes chances de decrire un ecran qui a bouge.
 * Le seuil est une alerte, pas un refus : le guide reste affiche entier.
 */
export const SEUIL_GUIDE_PERIME_JOURS = 90;

/** Vrai quand la derniere verification d'un guide date de plus de trois mois. */
export function guidePerime(verifieLe: string | null, maintenant: number): boolean {
  const jours = joursDepuis(verifieLe, maintenant);
  return jours !== null && jours > SEUIL_GUIDE_PERIME_JOURS;
}

/**
 * La ligne de confiance d'un guide. Sans date dans l'en-tete, on le dit plutot que de
 * laisser croire que le guide a ete verifie un jour.
 */
export function verificationEnClair(verifieLe: string | null, maintenant: number): string {
  if (!verifieLe) return "guide sans date de vérification";
  const debut = `guide vérifié le ${formatDate(verifieLe)}`;
  return guidePerime(verifieLe, maintenant)
    ? `${debut}, il y a plus de trois mois : les écrans ont pu changer`
    : debut;
}

/** Ce que dit une relance : `jours` vient de l'API, negatif quand la date est passee. */
export function relanceEnClair(jours: number): string {
  if (jours < -1) return `échue depuis ${-jours} jours`;
  if (jours === -1) return "échue depuis 1 jour";
  if (jours === 0) return "aujourd'hui";
  if (jours === 1) return "demain";
  return `dans ${jours} jours`;
}

/** `4/10, plafond 7`. Sans plafond : `4/10`. Sans score : rien. */
export function scoreEnClair(score: number | null, plafond: number | null): string {
  if (score === null) return "";
  return plafond === null ? `${score}/10` : `${score}/10, plafond ${plafond}`;
}

/** Montant en centimes, tel quel : `1 250` centimes donnent `12,50 EUR`. */
export function euros(cents: number | null): string {
  if (cents === null) return "";
  return `${(cents / 100).toFixed(2).replace(".", ",")} EUR`;
}

/** `1 pari`, `2 paris` : le compte et son nom, accorde. Zero prend le singulier en francais. */
export function compter(nombre: number, singulier: string, pluriel: string): string {
  return `${nombre} ${nombre > 1 ? pluriel : singulier}`;
}

/**
 * Le nom court d'un pari, la ou le titre entier ne tient pas : ce qui precede la premiere
 * virgule ou la premiere parenthese. Les fiches s'intitulent « Nom, description » et le
 * scout met ses precisions entre parentheses. Quand rien ne coupe, ou que la coupe ne
 * laisserait presque rien, le titre reste entier.
 */
export function nomCourt(titre: string): string {
  const coupe = titre.search(/,| \(/);
  return coupe < 3 ? titre : titre.slice(0, coupe).trim();
}

/**
 * Ce que `nomCourt` laisse de cote : la description qui suit le nom. La carte d'un pari
 * pose le nom en titre et ce reste en dessous, ce qui evite un titre de quatre lignes.
 * Une description entierement parenthesee perd ses parentheses, devenues inutiles une fois
 * la phrase detachee de son nom.
 */
export function resteDuTitre(titre: string): string {
  const coupe = titre.search(/,| \(/);
  if (coupe < 3) return "";
  const reste = titre.slice(coupe).replace(/^,/, "").trim();
  // Les parentheses ne partent que si ce sont bien les memes : « (module) et le reste (v2) »
  // commence et finit par une parenthese sans pour autant etre parenthese en entier, et le
  // retrait rendrait une chaine desequilibree.
  const enEntier = reste.startsWith("(") && reste.indexOf(")") === reste.length - 1;
  return enEntier ? reste.slice(1, -1).trim() : reste;
}

/**
 * La cle du contrat n'a pas d'accent (`decision`, `reglage`) et la pastille l'affiche en
 * capitales : sans cette table on lirait DECISION, or une capitale garde son accent.
 */
const LIBELLES_TYPE: Record<TypePrerequis, string> = {
  secret: "secret",
  dns: "DNS",
  compte: "compte",
  achat: "achat",
  decision: "décision",
  lecture: "lecture",
  reglage: "réglage",
};

export function typeEnClair(type: TypePrerequis): string {
  return LIBELLES_TYPE[type];
}

/**
 * Le verdict tel que la table l'affiche : premiere lettre en capitale. Les fiches ecrivent
 * « GO build » ou « PARQUER : ... », le scout rend « tester » ou « refusée ».
 */
export function verdictEnClair(verdict: string | null): string {
  if (!verdict) return "";
  return verdict.charAt(0).toLocaleUpperCase("fr") + verdict.slice(1);
}
